import type {RadiusId} from "../../themes/builder-constants";
import type React from "react";

import {InputGroup, ListBox, Popover, Typography} from "@heroui/react";
import {ExpandIcon} from "@project/icons-web";

import {formRadiusOptions, radiusOptions} from "../../themes/builder-constants";

import {LockableLabel} from "./lockable-label";
import {useThemeBuilder} from "./theme-builder-context";

/* ------------------------------------------------------------------------------------------------
 * RadiusPopover — reusable popover for a single radius variable (also exported for panel use)
 * ------------------------------------------------------------------------------------------------ */
export interface RadiusPopoverProps {
  label: string;
  variable: "radius" | "formRadius";
  description?: string;
  options: typeof radiusOptions;
  value: RadiusId;
  onChange: (id: RadiusId) => void;
}

export function RadiusPopover({
  description,
  label,
  onChange,
  options,
  value,
  variable,
}: RadiusPopoverProps) {
  const current = options.find((o) => o.id === value);

  function handleSelectionChange(keys: Set<React.Key> | "all") {
    if (keys === "all") return;
    const id = [...keys][0] as RadiusId | undefined;

    if (id) onChange(id);
  }

  return (
    <Popover>
      <div className="flex flex-col gap-1">
        <LockableLabel label={label} variable={variable} />
        <Popover.Trigger>
          <InputGroup className="w-40 cursor-pointer">
            <InputGroup.Prefix className="w-10">
              <span className="text-muted text-base font-semibold">{current?.label}</span>
            </InputGroup.Prefix>
            <InputGroup.Input
              readOnly
              className="max-w-20 cursor-pointer capitalize"
              id={variable}
              name={variable}
              value={current?.description}
            />
            <InputGroup.Suffix className="w-10">
              <ExpandIcon className="size-3" />
            </InputGroup.Suffix>
          </InputGroup>
        </Popover.Trigger>
      </div>
      <Popover.Content className="w-76 rounded-3xl" placement="top">
        <Popover.Dialog className="p-3">
          <div className="mb-2 flex flex-col gap-0.5 pl-2">
            <Typography className="text-xs font-medium capitalize">{label}</Typography>
            {description && <Typography className="text-muted text-xs">{description}</Typography>}
          </div>
          <ListBox
            disallowEmptySelection
            aria-label={label}
            selectionMode="single"
            layout="grid"
            selectedKeys={new Set([value])}
            className="grid grid-cols-3 gap-2 px-0"
            onSelectionChange={handleSelectionChange}
          >
            {options.map((opt) => (
              <ListBox.Item
                key={opt.id}
                id={opt.id}
                textValue={opt.label}
                className="group border-separator-on-surface data-[selected=true]:border-foreground flex h-20.75 w-22 flex-col items-center justify-center gap-1.25 rounded-2xl border"
              >
                <span className="text-xl font-semibold">{opt.label}</span>
                <Typography className="text-muted group-data-[selected=true]:text-foreground text-xs capitalize">
                  {opt.description}
                </Typography>
              </ListBox.Item>
            ))}
          </ListBox>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/* ------------------------------------------------------------------------------------------------
 * RadiusSelector — combines radius + formRadius into a single section
 * ------------------------------------------------------------------------------------------------ */
export function RadiusSelector() {
  const {params, setParam} = useThemeBuilder();

  return (
    <div className="flex flex-col gap-3">
      <RadiusPopover
        label="Radius"
        variable="radius"
        description="Affects the overall UI, like menus and modals"
        options={radiusOptions}
        value={params.radius}
        onChange={(id) => setParam("radius", id)}
      />
      <RadiusPopover
        label="Radius Form"
        variable="formRadius"
        description="Affects form elements, like inputs and selects"
        options={formRadiusOptions}
        value={params.formRadius}
        onChange={(id) => setParam("formRadius", id)}
      />
    </div>
  );
}
